import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  SearchBar,
  RescheduleModal,
  ConfirmationModal,
  RatingPopup,
  useToast,
} from "../../components";
import { appointment, review } from "../../store";
import "./bookings.css";

const formatDateTimeRange = (date, startTime, endTime) => {
  const start = dayjs(`${date} ${startTime}`, "YYYY-MM-DD HH:mm").format(
    "MMM DD, YYYY h:mm A",
  );
  const end = dayjs(`${date} ${endTime}`, "YYYY-MM-DD HH:mm").format("h:mm A");
  return `${start} - ${end}`;
};

const isStartingSoon = (date, startTime) => {
  const now = dayjs();
  const sessionTime = dayjs(`${date} ${startTime}`, "YYYY-MM-DD HH:mm");
  const diff = sessionTime.diff(now, "minute");
  // Show "Starting Soon" from 10 mins before start time till start time - 1 min
  return diff > 0 && diff <= 10;
};

const isSessionActive = (date, startTime, endTime) => {
  const now = dayjs();
  const sessionStart = dayjs(`${date} ${startTime}`, "YYYY-MM-DD HH:mm");
  const sessionEnd = dayjs(`${date} ${endTime}`, "YYYY-MM-DD HH:mm");
  const enableTime = sessionStart.subtract(2, "minute");
  // Enable "Start Session" button at meeting start time - 2 mins
  return now >= enableTime && now < sessionEnd;
};

const canCancel = (date, startTime) => {
  const now = dayjs();
  const sessionTime = dayjs(`${date} ${startTime}`, "YYYY-MM-DD HH:mm");
  // Cancellation allowed only up to 10 minutes before start time
  return sessionTime.diff(now, "minute") > 10;
};

const canReschedule = (date, startTime) => {
  const now = dayjs();
  const sessionTime = dayjs(`${date} ${startTime}`, "YYYY-MM-DD HH:mm");
  // Rescheduling allowed only before the session starts
  return sessionTime.diff(now, "minute") > 0;
};

const PAGE_SIZE = 10;

const Bookings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();

  const rateAppointmentId =
    location.state?.showRatingAppointmentId ||
    searchParams.get("rateAppointmentId");
  const handledRatingRedirectRef = useRef(null);
  const {
    data: appointments,
    activeTab,
    isLoading,
    pagination,
  } = useSelector((state) => state.appointments);

  // State for reschedule modal
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);

  // State for confirmation modal
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);

  // State for rating popup
  const [isRatingPopupOpen, setIsRatingPopupOpen] = useState(false);
  const [ratingCoachData, setRatingCoachData] = useState(null);
  const [ratingExistingReview, setRatingExistingReview] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const hasMore = pagination?.hasMore ?? false;

  // Sub-filter state for Past tab
  const [pastType, setPastType] = useState("all");

  // Tick state to periodically re-evaluate session active window
  const [, setTick] = useState(0);

  // Re-render every 30s so "Start Session" button enables at start time
  // and disables at end time even if the page was loaded earlier
  useEffect(() => {
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Load data when component mounts or tab changes
  useEffect(() => {
    setPage(0);
    setPastType("all");
  }, [activeTab]);

  useEffect(() => {
    const skip = page * PAGE_SIZE;
    if (activeTab === "today") {
      dispatch(appointment.fetchTodayAppointments(PAGE_SIZE, skip));
    } else if (activeTab === "upcoming") {
      dispatch(appointment.fetchUpcomingAppointments(PAGE_SIZE, skip));
    } else if (activeTab === "past") {
      const pastTypeParam = pastType === "all" ? undefined : pastType;
      dispatch(
        appointment.fetchPastAppointments(PAGE_SIZE, skip, pastTypeParam),
      );
    } else {
      dispatch(appointment.fetchCompletedAppointments(PAGE_SIZE, skip));
    }
  }, [dispatch, activeTab, page, pastType]);

  const handleTabChange = (tab) => {
    dispatch(appointment.setActiveTab(tab));
  };

  // Map API data to UI format
  const formatBookingsForUI = (apiBookings) => {
    return apiBookings.map((booking) => {
      const startTime = booking.startTime;
      // Format date as "Jan 15, 2024 2:30 PM"
      const formattedDate = dayjs(`${booking.date} ${startTime}`).format(
        "MMM DD, YYYY h:mm A",
      );

      let oldDate = null;
      if (booking.originalAppointment) {
        const oldStartTime = booking.originalAppointment.startTime;
        oldDate = dayjs(
          `${booking.originalAppointment.date} ${oldStartTime}`,
        ).format("MMM DD, YYYY h:mm A");
      }

      return {
        id: booking.id, // Use the actual appointment ID from API
        title: booking.topic.name,
        coach: `${booking.coach.firstName} ${booking.coach.lastName}`.trim(),
        coachId: booking.coach.id,
        coachPicture: booking.coach.picture,
        date: formattedDate,
        rawDate: booking.date,
        rawStartTime: booking.startTime,
        rawEndTime: booking.endTime,
        oldDate: oldDate,
        icon: booking?.topic?.picture || "fitness_center", // Default icon, could be enhanced to use topic-specific icons
        isRescheduled: booking.appointmentStatus === "RESCHEDULED",
        isPast: activeTab === "past",
        topicId: booking.topic.id, // Store topic ID for reschedule navigation
      };
    });
  };

  const currentBookings = formatBookingsForUI(appointments);

  const handleBookNewSession = () => {
    navigate("/explore");
  };

  const handleReschedule = (appointmentId) => {
    // Open the reschedule modal
    setSelectedAppointmentId(appointmentId);
    setIsRescheduleModalOpen(true);
  };

  const handleRescheduleModalClose = () => {
    setIsRescheduleModalOpen(false);
    setSelectedAppointmentId(null);
  };

  const handleCancel = (appointmentId) => {
    // Set the appointment to cancel and open confirmation modal
    setAppointmentToCancel(appointmentId);
    setIsConfirmationModalOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!appointmentToCancel) return;

    try {
      // Dispatch cancel action with the appointment ID
      await dispatch(
        appointment.cancelAppointment({ appointmentId: appointmentToCancel }),
      ).unwrap();

      // Show success message
      toast.success("Appointment cancelled successfully");

      // Refresh the current tab data
      if (activeTab === "today") {
        dispatch(appointment.fetchTodayAppointments());
      } else if (activeTab === "upcoming") {
        dispatch(appointment.fetchUpcomingAppointments());
      } else {
        dispatch(appointment.fetchCompletedAppointments());
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      // Close the confirmation modal
      setIsConfirmationModalOpen(false);
      setAppointmentToCancel(null);
    }
  };

  const handleCancelModalClose = () => {
    setIsConfirmationModalOpen(false);
    setAppointmentToCancel(null);
  };

  const handleRatingClose = useCallback(() => {
    setIsRatingPopupOpen(false);
    setRatingCoachData(null);
    setRatingExistingReview(null);
  }, []);

  const clearRatingRedirectParams = useCallback(() => {
    navigate(location.pathname, { replace: true, state: {} });
    if (searchParams.get("rateAppointmentId")) {
      setSearchParams({}, { replace: true });
    }
  }, [location.pathname, navigate, searchParams, setSearchParams]);

  // When redirected from the video room, auto-open the coach rating popup
  useEffect(() => {
    if (!rateAppointmentId) return undefined;
    if (handledRatingRedirectRef.current === rateAppointmentId) return undefined;

    let cancelled = false;

    const openRatingPopup = async () => {
      try {
        const res = await dispatch(
          appointment.fetchAppointmentById({
            appointmentId: rateAppointmentId,
          }),
        ).unwrap();

        if (cancelled) return;

        const appt = res?.data;
        if (!appt?.coach?.id) return;

        const coachData = {
          id: appt.coach.id,
          name: `${appt.coach.firstName} ${appt.coach.lastName}`.trim(),
          picture: appt.coach.picture,
        };

        let existingReview = null;
        try {
          const reviewRes = await dispatch(
            review.getMyCoachReview({ coachId: coachData.id }),
          ).unwrap();
          existingReview = reviewRes?.data?.existingReview ?? null;
        } catch {
          // Still show popup after a completed session even if review status fails
        }

        if (cancelled) return;

        handledRatingRedirectRef.current = rateAppointmentId;
        setRatingCoachData(coachData);
        setRatingExistingReview(existingReview);
        setIsRatingPopupOpen(true);
        clearRatingRedirectParams();
      } catch {
        if (!cancelled) {
          clearRatingRedirectParams();
        }
      }
    };

    openRatingPopup();

    return () => {
      cancelled = true;
    };
  }, [rateAppointmentId, dispatch, clearRatingRedirectParams]);

  return (
    <div>
      <SearchBar />
      <div className="bookings-container">
        {/* Header */}
        <div className="bookings-header">
          <h1>My Bookings</h1>
          <button className="book-new-btn" onClick={handleBookNewSession}>
            <span>Book New Session</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="tabs-container">
          <div className="tabs-header">
            <div
              className={`tab-item ${activeTab === "today" ? "active" : ""}`}
              onClick={() => handleTabChange("today")}
            >
              <p>Today</p>
            </div>
            <div
              className={`tab-item ${activeTab === "upcoming" ? "active" : ""}`}
              onClick={() => handleTabChange("upcoming")}
            >
              <p>Upcoming</p>
            </div>
            <div
              className={`tab-item ${activeTab === "past" ? "active" : ""}`}
              onClick={() => handleTabChange("past")}
            >
              <p>Past</p>
            </div>
          </div>
        </div>

        {/* Past Type Filter */}
        {activeTab === "past" && (
          <div className="past-filter">
            <button
              className={`filter-btn ${pastType === "all" ? "active" : ""}`}
              onClick={() => {
                setPastType("all");
                setPage(0);
              }}
            >
              All
            </button>
            <button
              className={`filter-btn ${pastType === "COMPLETED" ? "active" : ""}`}
              onClick={() => {
                setPastType("COMPLETED");
                setPage(0);
              }}
            >
              Completed
            </button>
            <button
              className={`filter-btn ${pastType === "NOT_JOINED" ? "active" : ""}`}
              onClick={() => {
                setPastType("NOT_JOINED");
                setPage(0);
              }}
            >
              Not Joined
            </button>
          </div>
        )}

        {/* Bookings List */}
        <div className="bookings-list">
          {currentBookings.map((booking) => {
            const startingSoon =
              booking.rawDate &&
              booking.rawStartTime &&
              !booking.isPast &&
              isStartingSoon(booking.rawDate, booking.rawStartTime);
            const sessionActive =
              booking.rawDate &&
              booking.rawStartTime &&
              booking.rawEndTime &&
              !booking.isPast &&
              isSessionActive(
                booking.rawDate,
                booking.rawStartTime,
                booking.rawEndTime,
              );
            const cancelAllowed =
              booking.rawDate &&
              booking.rawStartTime &&
              !booking.isRescheduled &&
              !booking.isPast &&
              canCancel(booking.rawDate, booking.rawStartTime);
            const rescheduleAllowed =
              booking.rawDate &&
              booking.rawStartTime &&
              !booking.isRescheduled &&
              !booking.isPast &&
              canReschedule(booking.rawDate, booking.rawStartTime);
            return (
              <div
                key={booking.id}
                className={`booking-card${
                  startingSoon ? " booking-card-soon" : ""
                }`}
              >
                <div className="booking-info">
                  <div className="booking-icon-wrapper">
                    {booking.icon &&
                    (booking.icon.startsWith("http") ||
                      booking.icon.startsWith("https") ||
                      booking.icon.startsWith("/")) ? (
                      <img
                        src={booking.icon}
                        alt="Topic icon"
                        style={{
                          width: "30px",
                          height: "30px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <span
                        className="material-symbols-outlined"
                        style={{ fontSize: "30px" }}
                      >
                        {booking.icon}
                      </span>
                    )}
                  </div>
                  <div className="booking-details">
                    <p className="booking-title">{booking.title}</p>
                    <p
                      className="booking-coach"
                      style={{ cursor: "pointer", color: "#08949b" }}
                      onClick={() => navigate(`/coach/${booking.coachId}`)}
                    >
                      {booking.coach}
                    </p>
                  </div>
                </div>
                <div className="booking-actions-wrapper">
                  {booking.isRescheduled ? (
                    <div className="booking-time-info desktop-only">
                      <div className="old-time">{booking.oldDate}</div>
                      <div className="rescheduled-badge">Rescheduled</div>
                      <div>
                        {formatDateTimeRange(
                          booking.rawDate,
                          booking.rawStartTime,
                          booking.rawEndTime,
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="booking-time-info desktop-only">
                      <div>
                        {formatDateTimeRange(
                          booking.rawDate,
                          booking.rawStartTime,
                          booking.rawEndTime,
                        )}
                      </div>
                    </div>
                  )}
                  {!booking.isPast && (
                    <div className="start-session-wrapper">
                      {startingSoon && (
                        <div className="starting-soon-badge">Starting Soon</div>
                      )}
                      <button
                        className="start-session-btn"
                        disabled={!sessionActive}
                        onClick={() =>
                          window.open(`/video-room/${booking.id}`, "_blank")
                        }
                      >
                        <span className="material-symbols-outlined start-session-btn-icon">
                          videocam
                        </span>
                        Start Session
                      </button>
                    </div>
                  )}
                  {!booking.isPast &&
                    !booking.isRescheduled &&
                    (cancelAllowed || rescheduleAllowed) && (
                      <div className="dropdown-wrapper">
                        <button className="more-btn">
                          <span className="material-symbols-outlined">
                            more_vert
                          </span>
                        </button>
                        <div className="dropdown-menu">
                          {rescheduleAllowed && (
                            <button
                              className="dropdown-item"
                              onClick={() => handleReschedule(booking.id)}
                            >
                              Reschedule
                            </button>
                          )}
                          {cancelAllowed && (
                            <button
                              className="dropdown-item cancel"
                              onClick={() => handleCancel(booking.id)}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  {booking.isRescheduled &&
                    !booking.isPast &&
                    cancelAllowed && (
                      <div className="dropdown-wrapper">
                        <button className="more-btn">
                          <span className="material-symbols-outlined">
                            more_vert
                          </span>
                        </button>
                        <div className="dropdown-menu">
                          <button
                            className="dropdown-item cancel"
                            onClick={() => handleCancel(booking.id)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            );
          })}

          {/* Empty State */}
          {!isLoading && currentBookings.length === 0 && (
            <div className="empty-state">
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "48px", color: "#94a3b8" }}
              >
                event_busy
              </span>
              <h3>
                {activeTab === "today"
                  ? "No More Sessions Today"
                  : "No More Upcoming Sessions"}
              </h3>
              <p>
                You've attended all your scheduled sessions. Ready to book your
                next one?
              </p>
              <button className="book-new-btn" onClick={handleBookNewSession}>
                <span>Book a New Session</span>
              </button>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!isLoading && currentBookings.length > 0 && (
          <div className="pagination-bar">
            <button
              className="pagination-btn"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              <span className="material-symbols-outlined">chevron_left</span>
              Back
            </button>
            <button
              className="pagination-btn"
              disabled={!hasMore}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        )}
      </div>

      {/* Reschedule Modal */}
      <RescheduleModal
        isOpen={isRescheduleModalOpen}
        onClose={handleRescheduleModalClose}
        appointmentId={selectedAppointmentId}
        appointmentData={appointments.find(
          (a) => a.id === selectedAppointmentId,
        )}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleCancelModalClose}
        onConfirm={handleConfirmCancel}
        title="Are you sure you want to cancel the booking?"
        confirmText="Yes"
        cancelText="No"
      />

      {/* Rating Popup */}
      {ratingCoachData && (
        <RatingPopup
          isOpen={isRatingPopupOpen}
          onClose={handleRatingClose}
          coachData={ratingCoachData}
          existingReview={ratingExistingReview}
        />
      )}
    </div>
  );
};

export default Bookings;
